import React, { useEffect, useRef, useState } from "react";
import { createDecartClient, models } from "@decartai/sdk";
import { decartToken } from "../Services/ApiServices/chatServices";
import "bootstrap/dist/css/bootstrap.min.css";
import "./Styles/decart-vton.scss";

const CAMERA_STARTUP_TIMEOUT = 10000;
const VIDEO_READY_TIMEOUT = 5000;
const WS_CONNECT_TIMEOUT = 15000;
const WS_RETRY_ATTEMPTS = 3;
const WS_RETRY_BASE_DELAY = 1500;
const MODEL_FALLBACK_CHAIN = [
  "lucy-vton-latest",
  "lucy-vton-3",
  "lucy-latest",
  "lucy-2.5"
];

const isWsError = (err) => {
  const msg = err?.message || "";
  const name = err?.name || "";
  return (
    name === "WebSocket error" ||
    msg.includes("WebSocket") ||
    msg.includes("WebSocket error") ||
    msg.includes("socket") ||
    msg.includes("ECONNREFUSED") ||
    msg.includes("NetworkError")
  );
};

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const DecartRealtimeVTON = () => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const processedStreamRef = useRef(null);
  const realtimeClientRef = useRef(null);
  const clientRef = useRef(null);
  const startupAbortRef = useRef(false);
  const lastConnectErrorRef = useRef(null);

  const [isReady, setIsReady] = useState(false);
  const [isCameraOn, setIsCameraOn] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [cameraFacing, setCameraFacing] = useState("user");
  const [prompt, setPrompt] = useState("");
  const [garmentFile, setGarmentFile] = useState(null);
  const [error, setError] = useState(null);
  const [devices, setDevices] = useState({ video: [], audio: [] });

  const getDefaultVTONPrompt = () =>
    "Substitute the current top with a navy blue hoodie with a white cross logo on the chest";

  useEffect(() => {
    initDecart();
    enumerateDevices();
  }, []);

  const enumerateDevices = async () => {
    try {
      const allDevices = await navigator.mediaDevices.enumerateDevices();
      setDevices({
        video: allDevices.filter((d) => d.kind === "videoinput"),
        audio: allDevices.filter((d) => d.kind === "audioinput"),
      });
    } catch (err) {
      console.warn("Device enumeration failed:", err);
    }
  };

  const initDecart = () => {
    decartToken(
      (response) => {
        if (response?.data?.client_token) {
          clientRef.current = createDecartClient({
            apiKey: response.data.client_token,
          });
        }
      },
      (err) => {
        console.error("Token error:", err);
        setError("Failed to initialize Decart client");
      }
    );
  };

  const requestMediaWithTimeout = async (constraints, timeoutMs) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        ...constraints,
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      return stream;
    } catch (err) {
      clearTimeout(timeoutId);
      if (err.name === "AbortError") {
        throw new Error(
          `Camera startup timed out after ${timeoutMs / 1000}s. The camera may be unavailable, in use, or blocked in this environment.`
        );
      }
      throw err;
    }
  };

  const waitForVideoReady = async (videoEl, timeoutMs) => {
    if (!videoEl) {
      throw new Error("Video element is not available");
    }
    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        cleanupVideoReadyListeners(videoEl, onLoadedMeta, onCanPlay, onError);
        reject(
          new Error(
            "Video stream did not become ready. The camera may not be delivering frames in this environment."
          )
        );
      }, timeoutMs);

      const onLoadedMeta = async () => {
        try {
          await videoEl.play();
          cleanupVideoReadyListeners(videoEl, onLoadedMeta, onCanPlay, onError);
          clearTimeout(timeoutId);
          resolve();
        } catch (playErr) {
          cleanupVideoReadyListeners(videoEl, onLoadedMeta, onCanPlay, onError);
          clearTimeout(timeoutId);
          reject(
            new Error(
              "Video metadata loaded but playback failed: " + playErr.message
            )
          );
        }
      };

      const onCanPlay = () => {
        cleanupVideoReadyListeners(videoEl, onLoadedMeta, onCanPlay, onError);
        clearTimeout(timeoutId);
        resolve();
      };

      const onError = (e) => {
        cleanupVideoReadyListeners(videoEl, onLoadedMeta, onCanPlay, onError);
        clearTimeout(timeoutId);
        reject(
          new Error("Video element encountered an error while loading the stream.")
        );
      };

      videoEl.addEventListener("loadedmetadata", onLoadedMeta);
      videoEl.addEventListener("canplay", onCanPlay);
      videoEl.addEventListener("error", onError);
    });
  };

  const cleanupVideoReadyListeners = (videoEl, ...listeners) => {
    videoEl?.removeEventListener("loadedmetadata", listeners[0]);
    videoEl?.removeEventListener("canplay", listeners[1]);
    videoEl?.removeEventListener("error", listeners[2]);
  };

  const connectToDecart = async (stream, initialState) => {
    lastConnectErrorRef.current = null;

    for (let attempt = 0; attempt < WS_RETRY_ATTEMPTS; attempt++) {
      if (startupAbortRef.current) {
        throw new Error("Connection cancelled by user");
      }

      for (let modelIdx = 0; modelIdx < MODEL_FALLBACK_CHAIN.length; modelIdx++) {
        const modelName = MODEL_FALLBACK_CHAIN[modelIdx];
        const model = models.realtime(modelName);

        if (startupAbortRef.current) {
          throw new Error("Connection cancelled by user");
        }

        try {
          console.warn(
            `[DecartRealtimeVTON] Attempt ${attempt + 1}/${WS_RETRY_ATTEMPTS}, model: ${modelName}`
          );

          let connectionTimedOut = false;

          const timeoutPromise = new Promise((_, reject) => {
            setTimeout(() => {
              connectionTimedOut = true;
              reject(
                new Error(
                  `WebSocket connection timed out after ${WS_CONNECT_TIMEOUT / 1000}s`
                )
              );
            }, WS_CONNECT_TIMEOUT);
          });

          const connectPromise = new Promise((resolve, reject) => {
            clientRef.current.realtime.connect(stream, {
              model,
              mirror: "auto",
              initialState,
              onRemoteStream: (editedStream) => {
                processedStreamRef.current = editedStream;
                if (videoRef.current) {
                  videoRef.current.srcObject = editedStream;
                  videoRef.current.play().catch((e) =>
                    console.warn("Remote stream play failed:", e)
                  );
                }
                setIsReady(true);
              },
              onError: (e) => {
                console.error(
                  "[DETAILED DECART ERROR]",
                  JSON.stringify(
                    {
                      name: e?.name,
                      code: e?.code,
                      message: e?.message,
                      stack: e?.stack
                    },
                    null,
                    2
                  )
                );
              }
            }).then(resolve).catch(reject);
          });

          realtimeClientRef.current = await Promise.race([
            connectPromise,
            timeoutPromise,
          ]);

          if (realtimeClientRef.current) {
            console.log(
              `[DecartRealtimeVTON] Connected successfully on model: ${modelName}`
            );
            return modelName;
          }
        } catch (err) {
          lastConnectErrorRef.current = err;
          console.error(
            `[DecartRealtimeVTON] Connect failed (model: ${modelName}):`,
            err?.message || err
          );

          if (realtimeClientRef.current) {
            try {
              realtimeClientRef.current.disconnect();
            } catch (disconnectErr) {
              console.warn("Error during disconnect cleanup:", disconnectErr);
            }
            realtimeClientRef.current = null;
          }

          if (isWsError(err) && modelIdx < MODEL_FALLBACK_CHAIN.length - 1) {
            console.warn(
              `[DecartRealtimeVTON] WebSocket failed for model ${modelName}, trying fallback...`
            );
            continue;
          }
        }
      }

      if (startupAbortRef.current) {
        throw new Error("Connection cancelled by user");
      }

      if (attempt < WS_RETRY_ATTEMPTS - 1) {
        const delay =
          WS_RETRY_BASE_DELAY * Math.pow(2, attempt) +
          Math.random() * 500;
        console.warn(
          `[DecartRealtimeVTON] Retrying all models in ${Math.round(delay)}ms...`
        );
        await sleep(delay);
      }
    }

    throw (
      lastConnectErrorRef.current ||
      new Error("Failed to connect to Decart after all retries")
    );
  };

  const startCamera = async () => {
    if (!clientRef.current) {
      setError("Decart client not initialized");
      return;
    }

    startupAbortRef.current = false;
    setError(null);
    setIsReady(false);
    setIsStarting(true);

    let stream = null;

    try {
      const model = models.realtime(MODEL_FALLBACK_CHAIN[0]);

      const buildVideoConstraints = (sourceModel) => {
        const constraints = {
          facingMode: cameraFacing,
        };

        if (Number.isFinite(sourceModel.fps)) {
          constraints.frameRate = { ideal: sourceModel.fps };
        }
        if (Number.isFinite(sourceModel.width)) {
          constraints.width = { ideal: sourceModel.width };
        }
        if (Number.isFinite(sourceModel.height)) {
          constraints.height = { ideal: sourceModel.height };
        }

        return constraints;
      };

      try {
        stream = await requestMediaWithTimeout(
          {
            video: buildVideoConstraints(model),
            audio: true,
          },
          CAMERA_STARTUP_TIMEOUT
        );
      } catch (audioError) {
        if (startupAbortRef.current) return;
        console.warn(
          "Audio not available, falling back to video-only:",
          audioError
        );
        stream = await requestMediaWithTimeout(
          {
            video: buildVideoConstraints(model),
            audio: false,
          },
          CAMERA_STARTUP_TIMEOUT
        );
      }

      if (startupAbortRef.current) {
        if (stream) stream.getTracks().forEach((t) => t.stop());
        return;
      }

      streamRef.current = stream;

      const videoEl = videoRef.current;
      if (videoEl) {
        videoEl.srcObject = stream;
        await waitForVideoReady(videoEl, VIDEO_READY_TIMEOUT);
      }

      if (startupAbortRef.current) {
        if (stream) stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        if (videoEl) videoEl.srcObject = null;
        return;
      }

      let initialState = {
        prompt: {
          text: prompt?.trim()?.length > 0 ? prompt : getDefaultVTONPrompt(),
          enhance: false,
        },
      };

      if (garmentFile) {
        const garmentBlob = new Blob(
          [await garmentFile.arrayBuffer()],
          { type: garmentFile.type }
        );
        initialState.image = garmentBlob;
      }

      await connectToDecart(stream, initialState);
      const connectedModel = realtimeClientRef.current ? "active" : "unknown";
      console.log(
        `[DecartRealtimeVTON] Camera startup complete. Realtime client is active.`
      );

      if (startupAbortRef.current) {
        if (stream) stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        return;
      }

      if (!realtimeClientRef.current) {
        throw new Error("Failed to connect to Decart realtime");
      }

      setIsCameraOn(true);
      setIsStarting(false);
      await enumerateDevices();
    } catch (err) {
      setIsStarting(false);
      setIsCameraOn(false);
      console.error("Camera error:", err);
      let errorMessage = err?.message || "Failed to start camera";
      if (err?.name === "NotReadableError") {
        errorMessage =
          "Could not start video source. Camera may be in use by another application or unavailable. Close other apps using the camera and try again.";
      } else if (err?.name === "NotAllowedError") {
        errorMessage =
          "Camera/microphone access denied. Please allow permissions.";
      } else if (err?.name === "NotFoundError") {
        errorMessage =
          "No camera found. If you are using a remote desktop or virtual environment, camera passthrough may not be available.";
      } else if (
        err?.name === "TypeError" &&
        err?.message?.includes("insecure")
      ) {
        errorMessage =
          "Camera access blocked: Application must be served over HTTPS.";
      } else if (err?.message?.includes("timed out")) {
        errorMessage =
          "Camera startup timed out. If you are in a remote desktop or virtual session, camera passthrough may not be configured. You can try switching cameras or using a different device.";
      } else if (isWsError(err)) {
        errorMessage =
          "WebSocket connection to Decart failed. The model may be unavailable in your region, or network/firewall settings may block WebSocket connections. Try a different network or contact support.";
      }
      setError(errorMessage);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    }
  };

  const cancelStarting = () => {
    startupAbortRef.current = true;
    setIsStarting(false);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setIsCameraOn(false);
    setIsReady(false);
  };

  const updateOutfit = async () => {
    if (!realtimeClientRef.current || !isReady) return;

    try {
      const updatePayload = {
        prompt: { text: prompt && prompt.trim().length > 0 ? prompt : "" },
      };

      if (garmentFile) {
        const garmentBlob = new Blob(
          [await garmentFile.arrayBuffer()],
          { type: garmentFile.type }
        );
        updatePayload.image = garmentBlob;
      }

      await realtimeClientRef.current.set(updatePayload);
    } catch (err) {
      console.error("Update error:", err);
      setError(err?.message || "Failed to update outfit");
    }
  };

  const stopCamera = () => {
    startupAbortRef.current = true;
    if (realtimeClientRef.current) {
      realtimeClientRef.current.disconnect();
      realtimeClientRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    processedStreamRef.current = null;
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setIsCameraOn(false);
    setIsReady(false);
    setIsStarting(false);
  };

  const switchCamera = async () => {
    stopCamera();
    setCameraFacing((p) => (p === "user" ? "environment" : "user"));
    await startCamera();
  };

  return (
    <div className="decart-vton-wrapper">
      <video ref={videoRef} autoPlay playsInline muted />

      {!isCameraOn && (
        <div className="vton-input-panel">
          <div className="card shadow-lg">
            <div className="card-body">
              <h2 className="text-center mb-3">Virtual Try-On (Realtime)</h2>

              <div className="mb-3">
                <label className="form-label">Garment Reference Image (Optional)</label>
                <input
                  type="file"
                  className="form-control"
                  accept=".jpg,.jpeg,.png,.gif,.webp"
                  onChange={(e) => setGarmentFile(e.target.files[0])}
                />
                {garmentFile && (
                  <small className="text-muted">Selected: {garmentFile.name}</small>
                )}
              </div>

              <div className="mb-3">
                <label className="form-label">Outfit Description</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="e.g., navy blue hoodie with white logo"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                />
              </div>

              {error && <div className="vton-error">{error}</div>}
              {devices.video.length === 0 && !error && (
                <div className="vton-warning">
                  No camera device detected. If you are in a remote desktop or
                  virtual environment, camera passthrough may not be available.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="vton-controls">
        {!isCameraOn ? (
          <button className="primary" onClick={startCamera}>
            Open Camera
          </button>
        ) : (
          <>
            <button className="secondary" onClick={switchCamera}>
              Switch
            </button>
            <button
              className="primary"
              onClick={updateOutfit}
              disabled={!isReady}
            >
              Update Outfit
            </button>
            <button className="secondary" onClick={stopCamera}>
              Close
            </button>
          </>
        )}
        {isStarting && (
          <button className="secondary" onClick={cancelStarting}>
            Cancel
          </button>
        )}
      </div>

      {isStarting && (
        <p className="vton-loading">Starting camera... Please allow camera access.</p>
      )}
      {!isReady && isCameraOn && !isStarting && (
        <p className="vton-loading">Initializing live AI try-on...</p>
      )}
      {isReady && isCameraOn && (
        <div className="vton-live-indicator">
          <div className="vton-live-dot"></div>
          LIVE
        </div>
      )}
    </div>
  );
};

export default DecartRealtimeVTON;
